import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { REPORT_IST } from './reportIst';

/** Default Field Tracker auto end: 8:00 PM IST. */
export const DEFAULT_DUTY_AUTO_END_MINUTES_IST = 20 * 60;

export const DUTY_AUTO_END_PREMIUM_CUSTOMIZE_MESSAGE =
  'Premium workspaces can customize this time. Free plan uses 8:00 PM IST.';

export function isFreeBillingPlan(billingPlan?: string): boolean {
  return (billingPlan ?? 'premium').toLowerCase() === 'free';
}

export function dutyAutoEndTimeEditable(billingPlan?: string): boolean {
  return !isFreeBillingPlan(billingPlan);
}

export function effectiveDutyAutoEndMinutesIst(params: {
  billingPlan?: string;
  companyMinutesIst?: number | null;
}): number {
  if (!dutyAutoEndTimeEditable(params.billingPlan)) {
    return DEFAULT_DUTY_AUTO_END_MINUTES_IST;
  }
  return clampDutyAutoEndMinutesIst(params.companyMinutesIst);
}

export function clampDutyAutoEndMinutesIst(minutes?: number | null): number {
  const m = minutes ?? DEFAULT_DUTY_AUTO_END_MINUTES_IST;
  return Math.min(23 * 60 + 59, Math.max(0, Math.round(m)));
}

export function formatDutyAutoEndMinutesIst(minutes: number): string {
  const m = clampDutyAutoEndMinutesIst(minutes);
  const h24 = Math.floor(m / 60);
  const min = m % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${String(min).padStart(2, '0')} ${period} IST`;
}

export function minutesIstFromTimeInput(value: string): number {
  const [h, min] = value.split(':').map((n) => Number(n));
  if (!Number.isFinite(h) || !Number.isFinite(min)) {
    return DEFAULT_DUTY_AUTO_END_MINUTES_IST;
  }
  return clampDutyAutoEndMinutesIst(h * 60 + min);
}

export function timeInputFromMinutesIst(minutes: number): string {
  const m = clampDutyAutoEndMinutesIst(minutes);
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function istDateKey(instant: Date): string {
  const z = toZonedTime(instant, REPORT_IST);
  const y = z.getFullYear();
  const mo = String(z.getMonth() + 1).padStart(2, '0');
  const d = String(z.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

function cutoffInstantForDateKey(dateKey: string, minutesIst: number): Date {
  const [y, mo, d] = dateKey.split('-').map((n) => Number(n));
  const m = clampDutyAutoEndMinutesIst(minutesIst);
  return fromZonedTime(
    new Date(y, mo - 1, d, Math.floor(m / 60), m % 60, 0, 0),
    REPORT_IST,
  );
}

function addDaysToDateKey(dateKey: string, days: number): string {
  const [y, mo, d] = dateKey.split('-').map((n) => Number(n));
  const base = new Date(y, mo - 1, d + days, 12, 0, 0);
  return istDateKey(base);
}

/** First company cutoff strictly after [startAt]. */
export function firstDutyAutoEndCutoffAfter(startAt: Date, minutesIst: number): Date {
  let dateKey = istDateKey(startAt);
  let cutoff = cutoffInstantForDateKey(dateKey, minutesIst);
  if (startAt.getTime() >= cutoff.getTime()) {
    dateKey = addDaysToDateKey(dateKey, 1);
    cutoff = cutoffInstantForDateKey(dateKey, minutesIst);
  }
  return cutoff;
}

/** When [now] is past the applicable cutoff, returns that cutoff instant. */
export function applicableDutyAutoEndCutoff(
  startAt: Date,
  now: Date,
  minutesIst: number,
): Date | null {
  const cutoff = firstDutyAutoEndCutoffAfter(startAt, minutesIst);
  if (now.getTime() >= cutoff.getTime()) return cutoff;
  return null;
}

function istMinutesFromMidnight(instant: Date): number {
  const z = toZonedTime(instant, REPORT_IST);
  return z.getHours() * 60 + z.getMinutes();
}

/** @deprecated Prefer [applicableDutyAutoEndCutoff] with session [startAt]. */
export function isAtOrPastDutyAutoEnd(now: Date, minutesIst: number): boolean {
  return istMinutesFromMidnight(now) >= clampDutyAutoEndMinutesIst(minutesIst);
}

/** @deprecated Prefer session-aware cutoff helpers. */
export function delayUntilDutyAutoEndMs(now: Date, minutesIst: number): number {
  const m = clampDutyAutoEndMinutesIst(minutesIst);
  if (isAtOrPastDutyAutoEnd(now, m)) return 0;
  const z = toZonedTime(now, REPORT_IST);
  const target = fromZonedTime(
    new Date(z.getFullYear(), z.getMonth(), z.getDate(), Math.floor(m / 60), m % 60, 0, 0),
    REPORT_IST,
  );
  return Math.max(0, target.getTime() - now.getTime());
}
